import React, { useState, useEffect } from 'react';
import { Modal, Input, Select, Button, Tag, Space, Alert } from 'antd';
import {
    RobotOutlined,
    CheckOutlined,
    EditOutlined,
    LinkOutlined,
    FolderOutlined,
    FileTextOutlined,
} from '@ant-design/icons';

const FOLDER_OPTIONS = [
    { value: '01', label: '01 - Requirements' },
    { value: '02', label: '02 - Resources' },
    { value: '03', label: '03 - Revisions' },
    { value: '04', label: '04 - Meeting Notes' },
    { value: '05', label: '05 - Deliverables' },
];

export default function AiFileOrganizationModal({
    open,
    file,
    aiData,
    onAccept,
    onChooseExistingFile,
    onCancel,
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [fileName, setFileName] = useState('');
    const [folderCode, setFolderCode] = useState('03');
    const [subfolderCategory, setSubfolderCategory] = useState('');
    const [revisionRound, setRevisionRound] = useState('');

    useEffect(() => {
        if (aiData) {
            setFileName(aiData.suggestedFileName || file?.name || '');
            setFolderCode(aiData.folderCode || '03');
            setSubfolderCategory(aiData.subfolderCategory || '');
            setRevisionRound(aiData.revisionRound || '');
            setIsEditing(false);
        }
    }, [aiData, file]);

    if (!aiData) return null;

    const handleConfirm = () => {
        onAccept({
            customFileName: fileName,
            folderCode,
            subfolderName: FOLDER_OPTIONS.find((f) => f.value === folderCode)?.label || '03 - Revisions',
            revisionRound: folderCode === '03' ? (revisionRound || 'Revision 01') : '',
            subfolderCategory,
            classification: aiData.classification,
        });
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            footer={null}
            centered
            width={620}
            title={
                <div className="flex items-center gap-3 text-slate-800 dark:text-white font-bold text-base">
                    <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
                        <RobotOutlined style={{ fontSize: 18 }} />
                    </span>
                    <span>AI File Organization</span>
                    {aiData.classification && (
                        <Tag color="blue" className="ml-2 font-bold px-2.5 py-0.5 text-xs">
                            {aiData.classification}
                        </Tag>
                    )}
                </div>
            }
        >
            <div className="space-y-4 py-2 text-sm">
                {/* AI Analysis Note */}
                <Alert
                    message={aiData.explanation || `This file appears to be a ${aiData.classification || 'project update'}.`}
                    type="info"
                    showIcon
                    className="rounded-xl text-xs sm:text-sm leading-relaxed"
                />

                {!isEditing ? (
                    /* Display View (Section 21 Spec) */
                    <div className="bg-slate-50 dark:bg-[#1a2538] border border-slate-200 dark:border-[#2a3852] rounded-xl p-5 space-y-4">
                        {/* Original File Name */}
                        <div>
                            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1">
                                Original Uploaded File
                            </span>
                            <p className="text-sm font-medium text-slate-700 dark:text-slate-200 flex items-center gap-2 bg-white dark:bg-[#141c2b] p-2.5 rounded-lg border border-slate-200/80 dark:border-[#2a3852]">
                                <FileTextOutlined className="text-slate-400 text-base" />
                                <span className="font-semibold text-slate-900 dark:text-white truncate" title={file?.name || aiData.originalName}>
                                    {file?.name || aiData.originalName || fileName}
                                </span>
                            </p>
                        </div>

                        {/* Suggested Standard Name */}
                        <div>
                            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1">
                                Suggested Standard Name
                            </span>
                            <p className="text-sm font-bold text-[#0070F3] dark:text-[#52a2ff] flex items-center gap-2 bg-blue-50/50 dark:bg-blue-950/30 p-2.5 rounded-lg border border-blue-200 dark:border-blue-900/50">
                                <FileTextOutlined className="text-blue-500 text-base" />
                                <span className="truncate" title={fileName}>
                                    {fileName}
                                </span>
                            </p>
                        </div>

                        {/* Destination Folder & Category */}
                        <div className="pt-3 border-t border-slate-200 dark:border-[#2a3852] grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1">
                                    Destination Folder
                                </span>
                                <p className="text-sm font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                                    <FolderOutlined className="text-amber-500 text-base" />
                                    <span>{FOLDER_OPTIONS.find((f) => f.value === folderCode)?.label}</span>
                                </p>
                            </div>
                            {(revisionRound || subfolderCategory) && (
                                <div>
                                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1">
                                        {folderCode === '03' ? 'Revision Round' : 'Category'}
                                    </span>
                                    <p className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                                        <Tag color={folderCode === '03' ? 'purple' : 'cyan'} className="font-bold text-xs px-2 py-0.5">
                                            {revisionRound || subfolderCategory}
                                        </Tag>
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    /* Edit Mode View */
                    <div className="space-y-4 bg-slate-50 dark:bg-[#141d2e] border border-slate-200 dark:border-[#2a3852] rounded-xl p-5">
                        {/* Original File Reference (Read-only) */}
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block mb-1">
                                Original File:
                            </label>
                            <div className="text-xs text-slate-600 dark:text-slate-300 bg-white dark:bg-[#1a2538] p-2 rounded-lg border border-slate-200 dark:border-[#2a3852] truncate">
                                {file?.name || aiData.originalName || fileName}
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 block mb-1">
                                Standardized File Name
                            </label>
                            <Input
                                value={fileName}
                                onChange={(e) => setFileName(e.target.value)}
                                placeholder="Enter standardized file name"
                                className="rounded-lg text-sm py-2"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 block mb-1">
                                Destination Folder (01 - 05)
                            </label>
                            <Select
                                value={folderCode}
                                onChange={(val) => setFolderCode(val)}
                                options={FOLDER_OPTIONS}
                                className="w-full h-10"
                            />
                        </div>

                        {folderCode === '03' ? (
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 block mb-1">
                                    Revision Round (e.g. Revision 01, Revision 02)
                                </label>
                                <Input
                                    value={revisionRound}
                                    onChange={(e) => {
                                        setRevisionRound(e.target.value);
                                        setSubfolderCategory(e.target.value);
                                    }}
                                    placeholder="Revision 01"
                                    className="rounded-lg text-sm py-2"
                                />
                            </div>
                        ) : (
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 block mb-1">
                                    Subfolder / Category (Optional)
                                </label>
                                <Input
                                    value={subfolderCategory}
                                    onChange={(e) => setSubfolderCategory(e.target.value)}
                                    placeholder="e.g. Technical Documentation, Samples, Milestone 01"
                                    className="rounded-lg text-sm py-2"
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Existing matches hint (Section 20 & 24) */}
                {aiData.existingMatches && aiData.existingMatches.length > 0 && (
                    <div className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-[#1f293d] p-3 rounded-lg flex items-center justify-between border border-slate-200/80 dark:border-slate-800">
                        <span className="truncate max-w-[320px]">
                            💡 Found {aiData.existingMatches.length} existing project file(s) in this category.
                        </span>
                        <Button
                            type="link"
                            size="small"
                            icon={<LinkOutlined />}
                            onClick={onChooseExistingFile}
                            className="p-0 text-blue-600 font-bold"
                        >
                            Reference instead
                        </Button>
                    </div>
                )}

                {/* Actions: [ Accept ] [ Edit ] [ Choose Existing File ] (Section 21) */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800">
                    <Button
                        onClick={onChooseExistingFile}
                        icon={<LinkOutlined />}
                        className="text-sm font-semibold rounded-lg h-9"
                    >
                        Choose Existing File
                    </Button>

                    <Space>
                        {!isEditing ? (
                            <Button
                                onClick={() => setIsEditing(true)}
                                icon={<EditOutlined />}
                                className="text-sm font-semibold rounded-lg h-9"
                            >
                                Edit
                            </Button>
                        ) : (
                            <Button
                                onClick={() => setIsEditing(false)}
                                className="text-sm font-semibold rounded-lg h-9"
                            >
                                Reset
                            </Button>
                        )}
                        <Button
                            type="primary"
                            onClick={handleConfirm}
                            icon={<CheckOutlined />}
                            className="text-sm font-bold rounded-lg h-9 bg-blue-600 hover:bg-blue-500 shadow-sm"
                        >
                            Accept
                        </Button>
                    </Space>
                </div>
            </div>
        </Modal>
    );
}
